/**
 * Sekce detailu uživatele v Identitách: Zabezpečení přihlášení (dvoufaktor:
 * příznak, výsledek, faktory, výjimka, reset) a Compliance (požadavky se stavem
 * a odkazy na normy, hodnoty s tím, kdo je potvrdil, potvrzení nové hodnoty).
 * Data: sso.mfa_user_set/mfa_reset, identity.compliance_*.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ClipboardCheck, ExternalLink, ShieldCheck } from 'lucide-react';
import { mcpCall } from '../lib/mcp';
import { Badge, Button, Input, Select } from '../components/ui';

interface MfaState {
  flag?: 'inherit' | 'optional' | 'required';
  exception?: { until: string; reason: string; by: string; at: string } | null;
  policy_mode?: string;
  effective?: string;
  kc_attr?: string;
  configured?: string[];
  setup_pending?: boolean;
  checked_at?: string;
}

const fmt = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('cs-CZ') : '—');

interface Me {
  identity?: { id?: string };
  capabilities?: { is_admin?: boolean };
}

function useMe() {
  const me = useQuery<Me>({ queryKey: ['identity-me'], queryFn: () => mcpCall('identity.me'), staleTime: 60_000 });
  return { myId: me.data?.identity?.id ?? null, isAdmin: me.data?.capabilities?.is_admin === true };
}

export function EntityMfaSection({ entityId, metadata, onChanged }: {
  entityId: string;
  metadata: Record<string, unknown>;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { myId, isAdmin } = useMe();
  const isSelf = myId === entityId;
  const mfa = (metadata.mfa ?? {}) as MfaState;
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: async (args: Record<string, unknown>) => {
      const r = await mcpCall<{ ok?: boolean; error?: string }>('sso.mfa_user_set', { entity: entityId, ...args });
      if (r?.ok === false) throw new Error(r.error ?? t('entityMfa.error'));
    },
    onSuccess: () => {
      onChanged();
      toast.success(t('entityMfa.saved'));
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const reset = useMutation({
    mutationFn: async () => {
      const r = await mcpCall<{ ok?: boolean; error?: string; removed?: number }>('sso.mfa_reset', { entity: entityId, reason: reason || t('entityMfa.resetDefaultReason') });
      if (r?.ok === false) throw new Error(r.error ?? t('entityMfa.error'));
      return r;
    },
    onSuccess: (r) => {
      onChanged();
      toast.success(`${t('entityMfa.resetDone')}: ${r?.removed ?? 0}`);
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const exceptionActive = !!mfa.exception?.until && Date.parse(mfa.exception.until) > Date.now();
  const effective = mfa.effective ?? 'off';
  const variant = effective === 'required' ? 'success' : effective === 'optional' ? 'info' : 'secondary';

  return (
    <div>
      <div className='mb-2 flex items-center gap-2'>
        <ShieldCheck size={14} className='text-gray-400' />
        <span className='text-sm font-medium text-white'>{t('entityMfa.title')}</span>
      </div>
      <div className='space-y-2 rounded bg-gray-900/40 p-2 text-xs'>
        <div className='flex flex-wrap items-center gap-2'>
          <Badge variant={variant}>{t(`entityMfa.effective_${effective}`)}</Badge>
          <span className='text-gray-500'>{t('entityMfa.policyMode')}: {t(`entityMfa.effective_${mfa.policy_mode ?? 'off'}`)}</span>
          <span className='text-gray-500'>· {t('entityMfa.kc')}: {mfa.kc_attr === 'force' ? t('entityMfa.kcForce') : t('entityMfa.kcSkip')}</span>
          {mfa.configured?.length
            ? <Badge variant='success'>{t('entityMfa.configured')}: {mfa.configured.join(', ')}</Badge>
            : <Badge variant='secondary'>{t('entityMfa.notConfigured')}</Badge>}
          {mfa.setup_pending && <Badge variant='warning'>{t('entityMfa.setupPending')}</Badge>}
          {exceptionActive && <Badge variant='warning'>{t('entityMfa.exceptionUntil')} {fmt(mfa.exception!.until)}</Badge>}
        </div>
        {(isSelf || isAdmin) && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-gray-400'>{t('entityMfa.flag')}</span>
            <Select
              size='sm'
              value={mfa.flag ?? 'inherit'}
              onChange={(e) => mut.mutate({ flag: e.target.value })}
              options={[
                { value: 'inherit', label: t('entityMfa.flag_inherit') },
                { value: 'optional', label: t('entityMfa.flag_optional') },
                { value: 'required', label: t('entityMfa.flag_required') },
              ]}
            />
            <span className='text-[11px] text-gray-500'>{t('entityMfa.flagHint')}</span>
          </div>
        )}
        {exceptionActive && mfa.exception && (
          <p className='text-gray-400'>
            {t('entityMfa.exception')}: {mfa.exception.reason} · {t('entityMfa.by')} {mfa.exception.by.slice(0, 8)}… · {fmt(mfa.exception.at)} → {fmt(mfa.exception.until)}
            {isAdmin && (
              <button type='button' className='ml-2 text-red-400 hover:underline' onClick={() => mut.mutate({ exception_clear: true })}>
                {t('entityMfa.exceptionClear')}
              </button>
            )}
          </p>
        )}
        {isAdmin && (
          <div className='flex flex-wrap items-center gap-2'>
            <Input type='date' value={until} onChange={(e) => setUntil(e.target.value)} className='text-xs' />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('entityMfa.reasonPlaceholder')} className='text-xs' />
            <Button size='sm' variant='secondary' disabled={mut.isPending || !until || reason.trim().length < 5} onClick={() => mut.mutate({ exception: { until, reason } })}>
              {t('entityMfa.exceptionGrant')}
            </Button>
            <Button size='sm' variant='secondary' disabled={reset.isPending || !(mfa.configured?.length)} onClick={() => reset.mutate()}>
              {t('entityMfa.reset')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface Evaluation {
  ok?: boolean;
  error?: string;
  failing: number;
  requirements: Array<{
    key: string;
    label: string;
    effect: string;
    status: string;
    shortfalls: Array<{ field: string; label: string; reason: string; kind: string }>;
    expires_at: string | null;
    controls: Array<{ norm: string; id: string; url: string | null; title: string | null }>;
    revision: string;
  }>;
}
interface Values {
  ok?: boolean;
  error?: string;
  values: Array<{
    id: string;
    field: string;
    label: string | null;
    value: unknown;
    confirmed_role: string;
    confirmed_at: string;
    confirmed_by: string | null;
    evidence_url: string | null;
    expires_at: string | null;
    note: string | null;
  }>;
}
interface Field {
  acm_uuid: string;
  key: string;
  label: string;
  type: string;
  options?: string[];
  evidence_required: boolean;
  confirmers: string[];
}

export function EntityComplianceSection({ entityId, tenantId }: { entityId: string; tenantId: string | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { isAdmin } = useMe();
  const evaluation = useQuery<Evaluation>({
    queryKey: ['compliance-evaluate', entityId],
    queryFn: () => mcpCall('identity.compliance_evaluate', { entity: entityId }),
  });
  const values = useQuery<Values>({
    queryKey: ['compliance-values', entityId],
    queryFn: () => mcpCall('identity.compliance_values', { entity: entityId }),
  });
  const fields = useQuery<{ fields?: Field[] }>({
    queryKey: ['compliance-fields', tenantId ?? ''],
    queryFn: () => mcpCall('identity.compliance_fields', tenantId ? { tenant: tenantId } : {}),
  });
  const [fieldKey, setFieldKey] = useState('');
  const [value, setValue] = useState('');
  const [evidence, setEvidence] = useState('');
  const [as, setAs] = useState('');
  const selected = (fields.data?.fields ?? []).find((f) => f.key === fieldKey);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['compliance-evaluate', entityId] });
    qc.invalidateQueries({ queryKey: ['compliance-values', entityId] });
  };
  const confirm = useMutation({
    mutationFn: async () => {
      const v = selected?.type === 'boolean' ? value === 'true' : value;
      const r = await mcpCall<{ ok?: boolean; error?: string; confirmers?: string[] }>('identity.compliance_value_set', {
        entity: entityId,
        field: fieldKey,
        value: v,
        ...(evidence ? { evidence_url: evidence } : {}),
        ...(as ? { as } : {}),
      });
      if (r?.ok === false) throw new Error(r.error ?? t('complianceUi.error'));
    },
    onSuccess: () => {
      refresh();
      setValue('');
      setEvidence('');
      toast.success(t('complianceUi.confirmed'));
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const r = await mcpCall<{ ok?: boolean; error?: string }>('identity.compliance_value_revoke', { id, reason: t('complianceUi.revokeReason') });
      if (r?.ok === false) throw new Error(r.error ?? t('complianceUi.error'));
    },
    onSuccess: refresh,
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const ev = evaluation.data;
  const vals = values.data;
  const statusVariant = (s: string) => (s === 'ok' ? 'success' : s === 'expired' ? 'warning' : 'error');

  return (
    <div>
      <div className='mb-2 flex items-center gap-2'>
        <ClipboardCheck size={14} className='text-gray-400' />
        <span className='text-sm font-medium text-white'>{t('complianceUi.sectionTitle')}</span>
        {ev && ev.ok !== false && (
          <Badge variant={ev.failing ? 'warning' : 'success'}>{ev.failing ? `${t('complianceUi.failing')}: ${ev.failing}` : t('complianceUi.ok')}</Badge>
        )}
      </div>
      <div className='space-y-2 rounded bg-gray-900/40 p-2 text-xs'>
        {ev?.ok === false && <p className='text-amber-400'>{ev.error}</p>}
        {ev && ev.ok !== false && ev.requirements.length === 0 && <p className='text-gray-500'>{t('complianceUi.noRequirements')}</p>}
        {ev?.requirements?.map((r) => (
          <div key={r.key} className='flex flex-wrap items-center gap-2'>
            <Badge variant={statusVariant(r.status)}>{t(`complianceUi.req_${r.status}`)}</Badge>
            <span className='text-gray-200'>{r.label}</span>
            {r.effect === 'block' && <Badge variant='error'>block</Badge>}
            {r.shortfalls.map((s, i) => <span key={i} className='text-gray-500'>{s.label}: {s.reason}</span>)}
            {r.expires_at && <span className='text-gray-600'>→ {fmt(r.expires_at)}</span>}
            {r.controls.map((c) => (
              c.url
                ? (
                  <a key={`${c.norm}${c.id}`} href={c.url} target='_blank' rel='noreferrer' className='font-mono text-[10px] text-plex hover:underline'>
                    {c.norm} {c.id} <ExternalLink size={9} className='inline' />
                  </a>
                )
                : <span key={`${c.norm}${c.id}`} className='font-mono text-[10px] text-gray-500'>{c.norm} {c.id}</span>
            ))}
          </div>
        ))}
        {vals?.values?.length ? (
          <div className='divide-y divide-gray-800/60 border-t border-gray-800/60 pt-1'>
            {vals.values.map((v) => (
              <div key={v.id} className='flex flex-wrap items-center gap-2 py-1'>
                <span className='font-medium text-gray-200'>{v.label ?? v.field}</span>
                <span className='font-mono text-gray-300'>{String(v.value)}</span>
                <span className='text-gray-500'>{t(`complianceUi.confirmer_${v.confirmed_role}`)} · {fmt(v.confirmed_at)}</span>
                {v.expires_at && <span className='text-gray-600'>→ {fmt(v.expires_at)}</span>}
                {v.evidence_url && (
                  <a href={v.evidence_url} target='_blank' rel='noreferrer' className='text-plex hover:underline'>
                    {t('complianceUi.evidence')} <ExternalLink size={9} className='inline' />
                  </a>
                )}
                {isAdmin && (
                  <button type='button' className='text-gray-500 hover:text-red-400' onClick={() => revoke.mutate(v.id)}>
                    {t('complianceUi.revoke')}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : null}
        <form
          className='flex flex-wrap items-center gap-2 border-t border-gray-800/60 pt-2'
          onSubmit={(e) => {
            e.preventDefault();
            if (fieldKey && value) confirm.mutate();
          }}
        >
          <Select
            size='sm'
            value={fieldKey}
            onChange={(e) => setFieldKey(e.target.value)}
            options={[{ value: '', label: t('complianceUi.chooseField') }, ...(fields.data?.fields ?? []).filter((f) => f.type !== 'system').map((f) => ({ value: f.key, label: f.label }))]}
          />
          {selected?.type === 'enum'
            ? <Select size='sm' value={value} onChange={(e) => setValue(e.target.value)} options={[{ value: '', label: '—' }, ...(selected.options ?? []).map((o) => ({ value: o, label: o }))]} />
            : selected?.type === 'boolean'
            ? <Select size='sm' value={value} onChange={(e) => setValue(e.target.value)} options={[{ value: '', label: '—' }, { value: 'true', label: t('complianceUi.yes') }, { value: 'false', label: t('complianceUi.no') }]} />
            : <Input type={selected?.type === 'date' ? 'date' : 'text'} value={value} onChange={(e) => setValue(e.target.value)} className='text-xs' placeholder={t('complianceUi.valuePlaceholder')} />}
          <Input value={evidence} onChange={(e) => setEvidence(e.target.value)} className='text-xs' placeholder={t('complianceUi.evidencePlaceholder')} />
          {selected && (
            <Select
              size='sm'
              value={as}
              onChange={(e) => setAs(e.target.value)}
              options={[{ value: '', label: t('complianceUi.asAuto') }, ...selected.confirmers.filter((c) => c !== 'system').map((c) => ({ value: c, label: t(`complianceUi.confirmer_${c}`) }))]}
            />
          )}
          <Button size='sm' type='submit' disabled={confirm.isPending || !fieldKey || !value || (selected?.evidence_required && !evidence)}>
            {t('complianceUi.confirm')}
          </Button>
        </form>
      </div>
    </div>
  );
}
